<template>
  <learn-layout>
    <template v-slot:content>
      <v-col :cols="12">

        <div class="text-h6 pt-6">Smartphone setup guide</div>
        <p>Please select the guide below based on the type of phone that you have.</p>

        <v-expansion-panels>
          <v-expansion-panel>
            <v-expansion-panel-header>
              <span class="text-h6">
                <v-icon class="pr-3">mdi-apple</v-icon>
                iPhone Guide
              </span>
            </v-expansion-panel-header>
            <v-expansion-panel-content>

              <v-alert type="info">
                If your camera prompts you to do a “Firmware update” once you connect to it, that means there’s a software update for the Camera. It normally takes 2-5 minutes to do.
              </v-alert>

              <div id="iphone">
                <div class="text-h6 pt-3">
                  iPhone instructions
                </div>
                <div class="embed-youtube">
                  <iframe width="560" height="315" src="https://www.youtube-nocookie.com/embed/2laNj0ZB4Ck" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
                </div>
                <div class="text-h6 pt-3">
                  Camera Setup
                </div>
                <ol>
                  <li>Take the sleeve off of your Insta360 camera and place in on a flat surface. Place the camera on top of the sleeve to protect the lens.</li>
                  <li>Turn your camera on by pressing and holding the button on the side until the light comes on.</li>
                  <li>For extra stability and safety, consider connecting your camera to your tripod.</li>
                </ol>
                <div class="text-h6 pt-3">
                  Phone Setup
                </div>
                <ol>
                  <li>Download the Insta360 app from the App Store: <a href="https://apps.apple.com/us/app/insta360/id1491299654" target="_blank">https://apps.apple.com/us/app/insta360/id1491299654</a></li>
                  <li>Open the Insta360 application.</li>
                </ol>
              </div>

              <div class="text-h6 pt-3">Configure Your Camera Settings</div>
              <p>Now that you have everything ready, it’s time to connect your camera and your phone.</p>
              <ol>
                <li>
                  Open the Insta360 Application and answer the onboarding questions
                  <ul>
                    <li>Do you have an Insta360 camera? Yes.</li>
                    <li>Network, Bluetooth, & Photo Album permissions? Allow.</li>
                    <li>Local Network & Notifications? Allow.</li>
                  </ul>
                </li>
                <li>Begin the search for devices in the Insta360 application. Your camera should pop up automatically. If you’re having trouble here, double check to make sure that your camera is on.</li>
                <li>Once you find and select your camera from the Insta360 app, look at your Insta360 camera screen. It should prompt you to accept the connection. Tap the checkbox on the camera screen to complete the connection.</li>
                <li>On your phone, allow Insta360 to access the wifi network (the application will prompt you for this).</li>
              </ol>

              <div class="text-h6 pt-3">Record a Test Video</div>
              <p>Before you record your first tour, do a test video to make sure your camera settings are correctly configured.</p>
              <ol>
                <li>Open the Insta360 app and tap the camera icon at the bottom. This will open a preview of the live camera feed.</li>
                <li>
                  Check to ensure that your camera is in the proper mode with the following settings:
                  <ul>
                    <li>Bottom of screen: Make sure you are in “Video” mode</li>
                    <li>Side of screen: Set video style to “360°”</li>
                    <li>Top of screen: Change video resolution to “4k” and “30fps”</li>
                    <li>Top Right Corner, tap the menu: Enable GPS Data (helpful for neighborhood tours)</li>
                  </ul>
                </li>
                <li>Tap the red button to begin your test recording. </li>
                <li>Tap the red button again to end your test recording</li>
              </ol>

              <br />
              <v-alert type="info">
                We recommend you practice this a few times.  Especially the night before you capture your first tour or two.  Practice makes perfect!
              </v-alert>

              <p>Once you’re finished recording for the day, you can prepare to upload your videos to Torvue with our Uploader program. The next article will walk you through capturing a tour.</p>

            </v-expansion-panel-content>
          </v-expansion-panel>
          <v-expansion-panel>
            <v-expansion-panel-header>
              <span class="text-h6">
                <v-icon class="pr-3">mdi-android</v-icon>
                Android Guide
              </span>
            </v-expansion-panel-header>
            <v-expansion-panel-content>

              <v-alert type="info">
                If your camera prompts you to do a “Firmware update” once you connect to it, that means there’s a software update for the Camera. It normally takes 2-5 minutes to do.
              </v-alert>

              <div id="android">
                <div class="text-h6 pt-3">
                  Android instructions
                </div>
                <div class="embed-youtube">
                  <iframe width="560" height="315" src="https://www.youtube-nocookie.com/embed/41uA4D6O6sI" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
                </div>
                <div class="text-h6 pt-3">
                  Camera Setup
                </div>
                <ol>
                  <li>Take the sleeve off of your Insta360 camera and place in on a flat surface. Place the camera on top of the sleeve to protect the lens.</li>
                  <li>Turn your camera on by pressing and holding the button on the side until the light comes on.</li>
                  <li>For extra stability and safety, consider connecting your camera to your tripod.</li>
                </ol>
                <div class="text-h6 pt-3">
                  Phone Setup
                </div>
                <ol>
                  <li>Download the Insta360 app from the Google Play Store: <a href="https://play.google.com/store/apps/details?id=com.arashivision.insta360akiko" target="_blank">https://play.google.com/store/apps/details?id=com.arashivision.insta360akiko</a></li>
                  <li>Open the Insta360 application.</li>
                </ol>

              </div>

              <div class="text-h6 pt-3">Configure Your Camera Settings</div>
              <p>Now that you have everything ready, it’s time to connect your camera and your phone.</p>
              <ol>
                <li>
                  Open the Insta360 Application and answer the onboarding questions
                  <ul>
                    <li>Do you have an Insta360 camera? Yes.</li>
                    <li>Network, Bluetooth, & Photo Album permissions? Allow.</li>
                    <li>Local Network & Notifications? Allow.</li>
                  </ul>
                </li>
                <li>Begin the search for devices in the Insta360 application. Your camera should pop up automatically. If you’re having trouble here, double check to make sure that your camera is on.</li>
                <li>Once you find and select your camera from the Insta360 app, look at your Insta360 camera screen. It should prompt you to accept the connection. Tap the checkbox on the camera screen to complete the connection.</li>
                <li>On your phone, allow Insta360 to access the wifi network (the application will prompt you for this).</li>
              </ol>

              <div class="text-h6 pt-3">Record a Test Video</div>
              <p>Before you record your first tour, do a test video to make sure your camera settings are correctly configured.</p>
              <ol>
                <li>Open the Insta360 app and tap the camera icon at the bottom. This will open a preview of the live camera feed.</li>
                <li>
                  Check to ensure that your camera is in the proper mode with the following settings:
                  <ul>
                    <li>Bottom of screen: Make sure you are in “Video” mode</li>
                    <li>Side of screen: Set video style to “360°”</li>
                    <li>Top of screen: Change video resolution to “4k” and “30fps”</li>
                    <li>Top Right Corner, tap the menu: Enable GPS Data (helpful for neighborhood tours)</li>
                  </ul>
                </li>
                <li>Tap the red button to begin your test recording. </li>
                <li>Tap the red button again to end your test recording</li>
              </ol>

              <br />
              <v-alert type="info">
                We recommend you practice this a few times.  Especially the night before you capture your first tour or two.  Practice makes perfect!
              </v-alert>

              <p>Once you’re finished recording for the day, you can prepare to upload your videos to Torvue with our Uploader program. The next article will walk you through capturing a tour.</p>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-col>
    </template>
    <template v-slot:footer>
      <div>
        <div class="caption font-italic">Previous step:</div>
        <v-btn class="float-end" color="secondary" outlined to="/learn/unbox" :small="$vuetify.breakpoint.smAndDown">
          <v-icon class="pr-2">mdi-arrow-left</v-icon>
          Camera Assembly
        </v-btn>
      </div>
      <v-spacer />
      <div>
        <div class="caption font-italic">Next step:</div>
        <v-btn class="float-end" color="primary" to="/learn/capture" :small="$vuetify.breakpoint.smAndDown">
          Capture Tour
          <v-icon class="pl-2">mdi-arrow-right</v-icon>
        </v-btn>
      </div>
    </template>
  </learn-layout>
</template>

<script>
import LearnLayout from "@/components/LearnLayout.vue";

export default {
  name: "Mobile",
  components: {LearnLayout},
  data() {
    return {
      phone: null
    }
  },
  methods: {
    filterChanged(v) {
      this.filters = v
    }
  }
}
</script>

<style scoped>

</style>